/* prettier-ignore */

import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { NewCustomRouteService } from '../../new-custom-route.service';
import { SelectDayComponent } from './components/select-day/select-day.component';
import { MAP_CONFIG, MAP_STYLE, PRIMARY_TIMELINE_COLORS } from '../../constants';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GoogleMapsService } from './google-map.service';
import { AddConfirmComponent } from './components/add-confirm/add-confirm.component';
import { CardsMobileMapComponent } from './components/mobile-cards/mobile-cards.component';
import { ActionButtonsComponent } from './components/action-buttons/action-buttons.component';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { CommonModule } from '@angular/common';
import { DebugService } from 'src/app/services/debug.service';
declare const google: any;

@Component({
  selector: 'map-custom-route',
  imports: [
    CommonModule,
    SelectDayComponent,
    ActionButtonsComponent,
    AddConfirmComponent,
    CardsMobileMapComponent,
    TranslateModule,
  ],
  standalone: true,
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class MapCustomRouteComponent {
    @ViewChild('mapElement', { static: true }) mapElement?: ElementRef;
    private routeService = inject(NewCustomRouteService);
    public translate = inject(TranslateService);
    private debugService = inject(DebugService);
    private googleMapsService = inject(GoogleMapsService);

    private customRoute = computed(() => this.routeService.route());
    private showPolylines = computed(() => this.routeService.showPolylines())
    public days = computed(() => Array(this.routeService.route().length).fill({}));


    private markerClusterer: MarkerClusterer | null = null;
    
    private initialStartPoint = computed(() => ({
      lat: this.customRoute()?.[0]?.startPoint?.latitude, 
      lng: this.customRoute()?.[0]?.startPoint?.longitude
    }))

    public mobileCardColor = signal(null)
    private selectedDay = computed(() =>  this.routeService.selectedDay());
    public disableTouch = computed(() => this.routeService.disableTouch())
    public pointsPerDay = computed(() => {
      const points = this.customRoute()?.[this.selectedDay()].points.map(item => item.point)?.filter(item => item);
      return points || [];
    })

    public getColor = effect(() => {
      this.mobileCardColor.set(PRIMARY_TIMELINE_COLORS[this.routeService.selectedDay()])
    }, {allowSignalWrites: true})

    private reloadMap = effect(() => {
      this.debugService.info('Reloading Map', '[MAP COMPONENT]');
      const route = this.routeService.route();
      this.loadDayMarkers(this.selectedDay())
      this.googleMapsService.fitBounds();
    });

    private reloadByEffect = effect(() => {
      const reload = this.routeService.reloadMapEffect();
      this.loadDayMarkers(this.selectedDay())
      this.googleMapsService.fitBounds();
    })

    private addItem = effect(() => {
      const item = this.routeService.itemToAdd()?.item;
      if (!item) 
        return; 

      this.addNewItem(item, this.routeService.previousLocation() || this.initialStartPoint())
    }, {allowSignalWrites: true})

    public itemToAdd = computed(() => this.routeService.itemToAdd())

    private map: google.maps.Map;
    private infoWindow: google.maps.InfoWindow;
    private markerGroupsByKey: Record<string, google.maps.Marker[]> = {};
    private originalPositionByMarker = new Map<google.maps.Marker, google.maps.LatLngLiteral>();
    private isSpiderfied = false;

    ngAfterViewInit(): void {
      this.initMap();
    }

    public async initMap(): Promise<void> {
      if (!this.mapElement) return;
      this.map = await this.googleMapsService.initializeMap(this.mapElement.nativeElement, {
        ...MAP_CONFIG,
        center: this.initialStartPoint(),
        styles: MAP_STYLE.light,
        zoom: 13
      });

      this.map.addListener('click', () =>  this.handleMapClick());
      this.map.addListener('zoom_changed', () => {
        const zoom = this.map.getZoom() ?? 0;

        if (zoom >= 16 && !this.isSpiderfied) {
          Object.values(this.markerGroupsByKey).forEach(group => {
            if (group.length > 1) 
              this.spiderfyCluster(group, 10);
          });
          this.isSpiderfied = true;
        } 
        else if (zoom < 16 && this.isSpiderfied) {
          this.resetSpiderfy();
          this.isSpiderfied = false;
        }
      });
      this.loadDayMarkers(0);
    }

    private handleMapClick(): void {
      this.infoWindow?.close();
    }

    private async loadDayMarkers(day: number): Promise<void> {
      if (!this.map) 
        return;
    
      this.googleMapsService.clearMap();
      this.markerGroupsByKey = {};
      this.originalPositionByMarker.clear();
      this.isSpiderfied = false;

      const routeDay = this.customRoute()?.[day]?.points;
      const initialPoint = this.customRoute()?.[day]?.startPoint;
      const initialPointName = this.customRoute()?.[day]?.startPointName;
      const endPoint = this.customRoute()?.[day]?.endPoint;
      const endPointName = this.customRoute()?.[day]?.endPointName;
      if (!routeDay || routeDay.length === 0) 
        return;

      this.googleMapsService.resetItemsRouteCoordinates();
      let countIndex = 1;

      this.markerGroupsByKey = {};
      const markers: google.maps.Marker[] = [];

      const addMarkerToGroup = (marker: google.maps.Marker, lat: number, lng: number) => {
        const key = `${lat.toFixed(6)}_${lng.toFixed(6)}`; // agrupa por coordenada redondeada
        if (!this.markerGroupsByKey[key]) {
          this.markerGroupsByKey[key] = [];
        }
        this.markerGroupsByKey[key].push(marker);

        this.originalPositionByMarker.set(marker, { lat, lng });
      };

      for (let index = 0; index < routeDay.length; index++) {
        const item = routeDay[index];
    
        if (index === 0 && initialPoint) {
          const location = { lat: parseFloat(initialPoint.latitude), lng: parseFloat(initialPoint.longitude) };
          const marker = this.googleMapsService.createMarker(location, 'I', '#000');
          if (initialPointName) {
            marker.addListener('click', () => {
              this.handleStartEndMarkerClick(marker, initialPointName);
            });
          }
        }
    
        if (item.type === 'point' || item.type === 'event') {
          const location = { lat: parseFloat(item.point.latitude), lng: parseFloat(item.point.longitude) };
          const marker = this.googleMapsService.createMarker(location, countIndex, PRIMARY_TIMELINE_COLORS[day]);
          countIndex++;
          
          markers.push(marker);
          addMarkerToGroup(marker, location.lat, location.lng);

          marker.addListener('click', () => {
            this.handleItemMarkerClick(marker, item.point);
          });
        }
    
        if (index === routeDay.length - 1 && endPoint) {
          const location = { lat: parseFloat(endPoint.latitude), lng: parseFloat(endPoint.longitude) };
          const marker = this.googleMapsService.createMarker(location, 'F', '#000');
          if (endPointName) {
            marker.addListener('click', () => {
              this.handleStartEndMarkerClick(marker, endPointName);
            });
          }
        }
        // await this.wait(100); // 300ms de retraso entre marcadores
      }

      if (this.markerClusterer) {
        this.markerClusterer.clearMarkers();
        this.markerClusterer.setMap(null);
      }

      this.markerClusterer = new MarkerClusterer({
        map: this.map,
        markers,
        algorithmOptions: {
          maxZoom: 15
        },
      });
        
      this.googleMapsService.fitBounds();
      if (this.showPolylines())
        this.googleMapsService.getRoute(day);
    }

    private wait(ms: number): Promise<void> {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private handleStartEndMarkerClick(marker: google.maps.Marker, address: any): void {
      this.infoWindow?.close();
      this.infoWindow = new google.maps.InfoWindow({
        content: 
          `
            <div class="start-end-wrapper">
                <span>${address}</span>
            </div>
          `
      });
      this.infoWindow.open(this.map, marker);
    }
  
    private handleItemMarkerClick(marker: google.maps.Marker, item: any): void {
      this.infoWindow?.close();
      this.infoWindow = new google.maps.InfoWindow({
        content: 
          `
            <div id="pointInfo" class="point-info-wrapper">
              <div class="point-info">
                <img draggable="false" src="${item.cover_image}" onerror="this.onerror=null; this.src='/assets/imgs/imageDefault.png';" >
                <span>${item.name[this.translate.currentLang]}</span>
              </div>
            </div>
          `
      });
      this.infoWindow.open(this.map, marker);
      this.infoWindow.addListener("domready", () => {
        const pointInfo = document.getElementById("pointInfo");
        if (!pointInfo)  return;
        pointInfo.addEventListener("click",  () => { this.routeService.modalItemInfo(item, true) }) 
      });
    }

    addNewItem(item: any, previousLocation: any): void {
      this.routeService.disableTouch.set(true)
      const itemLocation = { lat: parseFloat(item.latitude), lng: parseFloat(item.longitude) }
      const distancePath = [
        { lat: parseFloat(previousLocation.lat), lng: parseFloat(previousLocation.lng) },
        { lat: itemLocation.lat, lng: itemLocation.lng },
      ];
      const distanceNewPoint = this.routeService.getDistanceBetween(
        parseFloat(previousLocation.lat), parseFloat(previousLocation.lng),
        parseFloat(item.latitude), parseFloat(item.longitude)
      )    

      this.googleMapsService.clearMap();
      this.googleMapsService.setDarkMapStyle();

      this.googleMapsService.createMarker(previousLocation, 'I', '#FFF', true);
      this.googleMapsService.createMarker(itemLocation, 'F', '#FFF', true);

      this.googleMapsService.drawPolylineBetweenStartEnd(distancePath);
      this.googleMapsService.drawCircle(distancePath[1], 150);
      this.googleMapsService.drawPolylineBorderOnCircle(distancePath[1], 150);
    
      this.addDistanceLabel(distancePath, distanceNewPoint);
    }

    private addDistanceLabel(distancePath: google.maps.LatLngLiteral[], distanceNewPoint: number) {
      const distanceNewPointInKM = (distanceNewPoint / 1000).toFixed(2);
      const distanceLabelMap = document.createElement('div');
      distanceLabelMap.className = 'distance-label';
      distanceLabelMap.innerHTML = `<span class="number-id">${distanceNewPointInKM} Km</span>`;
    
      const overlay = new google.maps.OverlayView();
      overlay.onAdd = () => {
        const panes = overlay.getPanes();
        if (panes) 
          panes.overlayMouseTarget.appendChild(distanceLabelMap);
      };
    
      overlay.draw = () => {
        const projection = overlay.getProjection();
        if (projection) {
          const startPx = projection.fromLatLngToDivPixel(new google.maps.LatLng(distancePath[0].lat, distancePath[0].lng));
          const endPx = projection.fromLatLngToDivPixel(new google.maps.LatLng(distancePath[1].lat, distancePath[1].lng));
      
          if (startPx && endPx) {
            const distancePx = Math.sqrt(Math.pow(endPx.x - startPx.x, 2) + Math.pow(endPx.y - startPx.y, 2));
            distanceLabelMap.style.left = `${endPx.x}px`;
            distanceLabelMap.style.top = `${endPx.y + 25}px`;
            distanceLabelMap.style.position = 'absolute';
            distanceLabelMap.style.transform = `translate(-50%, -50%)`; 
            const zoomLevel = this.map.getZoom() || 1;
            const visibilityThreshold = zoomLevel > 10 ? 75 : 50;
            if (distancePx < visibilityThreshold) 
              distanceLabelMap.style.display = 'none';
            else 
              distanceLabelMap.style.display = 'block';
          }
        }
      };
    
      overlay.onRemove = () => {
        if (distanceLabelMap.parentNode) 
          distanceLabelMap.parentNode.removeChild(distanceLabelMap);
      };
    
      overlay.setMap(this.map);
      this.googleMapsService.overlayView = overlay;
      this.googleMapsService.fitBounds();
      this.googleMapsService.addZoomListener();
    
      this.map.addListener('zoom_changed', () => overlay.draw());
      this.map.addListener('drag', () => overlay.draw());
    }

    private spiderfyCluster(markers: google.maps.Marker[], radiusMeters = 10) {
      if (!markers?.length) 
        return;

      const originalCenter = this.originalPositionByMarker.get(markers[0]);
      if (!originalCenter) return;

      const centerLat = originalCenter.lat;
      const centerLng = originalCenter.lng;

      const count = markers.length;

      const latRad = centerLat * Math.PI / 180;
      const metersPerDegLat = 111320;
      const metersPerDegLng = 111320 * Math.cos(latRad); 

      const dLat = radiusMeters / metersPerDegLat;
      const dLng = radiusMeters / metersPerDegLng;
      const startAngle = (count === 2) ? Math.PI / 2 : -Math.PI / 2;
      const angleStep = (2 * Math.PI) / count;

      markers.forEach((m, i) => {
        const angle = startAngle + i * angleStep;
        const lat = centerLat + dLat * Math.cos(angle);
        const lng = centerLng + dLng * Math.sin(angle);
        m.setPosition({ lat, lng });
      });
    }

    private resetSpiderfy(): void {
      this.originalPositionByMarker.forEach((pos, marker) => {
        marker.setPosition(pos);
      });
    }

}